// src/context/ReportPollingContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UserContext } from './UserContext';
import { NotificationContext } from './NotificationContext';
import { BASE_URL, IS_PRERENDERING } from '../utils/config'; // Import IS_PRERENDERING

export const ReportPollingContext = createContext();

export const ReportPollingProvider = ({ children }) => {
  const [pollingRequests, setPollingRequests] = useState({}); // Stores { requestId: { token, attempts } }
  const [reportMessages, setReportMessages] = useState({}); // Stores { requestId: { message, type } }
  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const startPollingReport = useCallback(
    (requestId) => {
      if (!user || !user.token) {
        console.error('[ReportPollingContext] Cannot start polling without user token.');
        addNotification('Error: Could not start report status check (auth missing).', 'error');
        return;
      }
      console.log(`[ReportPollingContext] Starting to poll for requestId: ${requestId}`);
      setPollingRequests((prev) => ({
        ...prev,
        [requestId]: { token: user.token, attempts: 0, lastStatus: 'processing' },
      }));
      setReportMessages((prev) => ({
        // Set initial processing message locally too
        ...prev,
        [requestId]: { message: 'Report generation initiated. Checking status...', type: 'info' },
      }));
    },
    [user, addNotification]
  );

  useEffect(() => {
    if (IS_PRERENDERING) {
      // Do not start the polling interval during prerendering
      return;
    }

    const intervalId = setInterval(async () => {
      if (Object.keys(pollingRequests).length === 0) {
        return;
      }

      console.log('[ReportPollingContext] Polling tick. Active requests:', Object.keys(pollingRequests));

      for (const requestId in pollingRequests) {
        const requestInfo = pollingRequests[requestId];
        if (!requestInfo || requestInfo.attempts >= 60) {
          // Max attempts (e.g., 60 * 10s = 10 minutes)
          if (requestInfo) {
            addNotification(`Polling for report ${requestId} timed out. Please check your email or contact support.`, 'warning', 15000);
          }
          setReportMessages((prev) => ({
            ...prev,
            [requestId]: { message: `Polling for report ${requestId} timed out.`, type: 'warning' },
          }));
          setPollingRequests((prev) => {
            const { [requestId]: _, ...rest } = prev;
            return rest;
          });
          continue;
        }

        try {
          const response = await fetch(`${BASE_URL}/api/real-estate/report-status/${requestId}`, {
            headers: {
              Authorization: `Bearer ${requestInfo.token}`,
            },
          });

          if (!response.ok) {
            console.error(`[ReportPollingContext] Error fetching status for ${requestId}: ${response.statusText}`);
            // Potentially stop polling for this request if server error is persistent
            if (response.status === 404 || response.status === 401 || response.status === 403) {
              addNotification(`Could not retrieve status for report ${requestId}. It might have expired or there was an auth issue.`, 'error', 7000);
              setReportMessages((prev) => ({
                ...prev,
                [requestId]: {
                  message: `Could not retrieve status for report ${requestId}. It might have expired or there was an auth issue.`,
                  type: 'error',
                },
              }));
              setPollingRequests((prev) => {
                const { [requestId]: _, ...rest } = prev;
                return rest;
              });
            }
            continue;
          }

          const statusData = await response.json();
          console.log(`[ReportPollingContext] Polled status for ${requestId}:`, statusData);

          if (statusData.status === 'email_sent' || statusData.status === 'failed') {
            const isSuccess = statusData.status === 'email_sent';
            const message = statusData.message || `Report ${requestId} processing ${isSuccess ? 'completed' : 'failed'}.`;
            addNotification(message, isSuccess ? 'success' : 'error', 7000);

            setPollingRequests((prev) => {
              const { [requestId]: _, ...rest } = prev;
              return rest; // Remove from polling
            });
            setReportMessages((prev) => ({
              ...prev,
              [requestId]: {
                message: statusData.message || `Report ${requestId} processing ${statusData.status === 'email_sent' ? 'completed' : 'failed'}.`,
                type: statusData.status === 'email_sent' ? 'success' : 'error',
              },
            }));
          } else {
            // Update attempts or last known status if still processing
            setPollingRequests((prev) => ({ ...prev, [requestId]: { ...requestInfo, attempts: requestInfo.attempts + 1, lastStatus: statusData.status } }));
            setReportMessages((prev) => ({
              ...prev,
              [requestId]: {
                message: statusData.message || `Report ${requestId} is still ${statusData.status}...`,
                type: 'info', // Or map statusData.status to a type
              },
            }));
          }
        } catch (error) {
          console.error(`[ReportPollingContext] Network error polling for ${requestId}:`, error);
          // Potentially add a general notification about polling issues
        }
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(intervalId);
  }, [pollingRequests, addNotification, user]); // user dependency to re-evaluate if token changes (though pollingRequests already captures it)

  return <ReportPollingContext.Provider value={{ startPollingReport, pollingRequests, reportMessages }}>{children}</ReportPollingContext.Provider>;
};
